import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight, ArrowLeft, Check, Sparkles } from "lucide-react";

const AREAS = [
  "Career", "Finances", "Health", "Fitness", "Business", "Education",
  "AI Skills", "Productivity", "Personal Growth", "Travel", "Relationships", "Parenting",
];
const STYLES = ["supportive", "direct", "strategic", "encouraging", "accountable"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [f, setF] = useState({
    areas: [], goal: "", why: "", target_date: "", current_level: 5,
    time_available: "", budget: "", experience: "", obstacles: "",
    coaching_style: "supportive", motivators: "", checkin_time: "08:00",
  });

  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleArea = (a) => set("areas", f.areas.includes(a) ? f.areas.filter((x) => x !== a) : [...f.areas, a]);

  const steps = [
    { title: "What do you want to improve?", valid: f.areas.length > 0 },
    { title: "What's your biggest goal right now?", valid: f.goal.trim().length > 3 },
    { title: "Why does this goal matter to you?", valid: f.why.trim().length > 3 },
    { title: "A few details so we can tailor your plan", valid: true },
  ];

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding", { ...f, areas: f.areas.map((a) => a.toLowerCase().replace(/ /g, "_")) });
      setResult(data);
      if (!data.roadmap) toast.info("Goal saved. Your AI roadmap will be ready shortly — open the goal to generate it.");
      await refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not build your plan."));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const rm = result.roadmap || {};
    return (
      <div className="relative z-10 mx-auto flex min-h-screen max-w-2xl flex-col justify-center bg-[#FFF9F5] px-5 py-16 font-quicksand">
        <div className="rounded-3xl border border-[#FFE8E3] bg-white p-8">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sunrise text-[#2D3748]">
            <Sparkles className="h-6 w-6" />
          </div>
          <h1 className="font-nunito font-extrabold tracking-tight text-3xl text-[#2D3748]">Your Roadmap Is Ready</h1>
          <p className="mt-2 text-[#718096]">{result.goal?.title}</p>
          {rm.definition && <p className="mt-4 text-sm text-[#4A5568]">{rm.definition}</p>}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            {[["Milestones", rm.milestones?.length || 0], ["Habits", rm.habits?.length || 0], ["Week 1 tasks", (rm.first_week_plan || []).reduce((n, d) => n + (d.tasks?.length || 0), 0)]].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] py-4">
                <p className="font-nunito font-extrabold tracking-tight text-2xl text-sunrise">{v}</p>
                <p className="text-xs text-[#718096]">{l}</p>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/app")} data-testid="goto-dashboard-btn"
            className="mt-8 flex h-13 w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-bold tracking-wide text-[#2D3748] transition-all hover:scale-[1.02]">
            Go to My Dashboard <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center gap-4 bg-[#FFF9F5] font-quicksand text-[#718096]">
        <Loader2 className="h-10 w-10 animate-spin text-[#FF7E67]" />
        <p>Your AI coach is building your personalized roadmap…</p>
      </div>
    );
  }

  const cur = steps[step];

  return (
    <div className="relative z-10 mx-auto flex min-h-screen max-w-2xl flex-col justify-center bg-[#FFF9F5] px-5 py-16 font-quicksand">
      <div className="pointer-events-none absolute left-1/2 top-20 h-80 w-96 max-w-full -translate-x-1/2 glow-orange blur-3xl opacity-30" />
      <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
        <div className="h-full rounded-full bg-sunrise transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
      </div>
      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sunrise">Step {step + 1} of {steps.length}</p>
        <h1 className="mt-3 font-nunito text-2xl font-bold text-[#2D3748] sm:text-3xl">{cur.title}</h1>

        <div className="mt-6">
          {step === 0 && (
            <div className="flex flex-wrap gap-2.5" data-testid="areas">
              {AREAS.map((a) => (
                <button key={a} onClick={() => toggleArea(a)}
                  className={`rounded-full border px-4 py-2.5 text-sm transition-colors ${f.areas.includes(a) ? "border-transparent bg-sunrise text-[#2D3748]" : "border-[#FFE8E3] text-[#4A5568] hover:border-[#FF7E67]"}`}>
                  {a}
                </button>
              ))}
            </div>
          )}
          {step === 1 && (
            <textarea value={f.goal} onChange={(e) => set("goal", e.target.value)} rows={3} data-testid="goal-input"
              placeholder="e.g. Lose 30 pounds, save $10k, launch my SaaS…"
              className="w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] p-4 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          )}
          {step === 2 && (
            <textarea value={f.why} onChange={(e) => set("why", e.target.value)} rows={3} data-testid="why-input"
              placeholder="What will achieving this change for you?"
              className="w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] p-4 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          )}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm text-[#718096]">Target date
                  <input type="date" value={f.target_date} onChange={(e) => set("target_date", e.target.value)}
                    className="mt-1 h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
                </label>
                <label className="text-sm text-[#718096]">Daily check-in time
                  <input type="time" value={f.checkin_time} onChange={(e) => set("checkin_time", e.target.value)}
                    className="mt-1 h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
                </label>
              </div>
              <label className="block text-sm text-[#718096]">Current level: <span className="text-[#FF7E67]">{f.current_level}/10</span>
                <input type="range" min="1" max="10" value={f.current_level} onChange={(e) => set("current_level", Number(e.target.value))}
                  className="mt-2 w-full accent-[#FF7E67]" />
              </label>
              <input value={f.time_available} onChange={(e) => set("time_available", e.target.value)} placeholder="Time available (e.g. 5 hrs/week)"
                className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
              <input value={f.obstacles} onChange={(e) => set("obstacles", e.target.value)} placeholder="Known obstacles (optional)"
                className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
              <label className="block text-sm text-[#718096]">Preferred coaching style
                <select value={f.coaching_style} onChange={(e) => set("coaching_style", e.target.value)}
                  className="mt-1 h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 capitalize text-[#2D3748] outline-none focus:border-[#FF7E67]">
                  {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="flex items-center gap-1 text-sm text-[#718096] disabled:opacity-30">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {step < steps.length - 1 ? (
            <button onClick={() => cur.valid ? setStep(step + 1) : toast.error("Please complete this step.")} data-testid="next-btn"
              className="flex items-center gap-2 rounded-full bg-sunrise px-7 py-3 text-sm font-bold tracking-wide text-[#2D3748] transition-all hover:scale-[1.03]">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={submit} data-testid="finish-onboarding-btn"
              className="flex items-center gap-2 rounded-full bg-sunrise px-7 py-3 text-sm font-bold tracking-wide text-[#2D3748] transition-all hover:scale-[1.03]">
              Build My Plan <Check className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
